// Friends + friend-requests API surface (spec #20 / #21).
import { fetchJson, fetchJsonList } from './http';
import type {
  FriendEntry,
  FriendListResponse,
  FriendRequestEntry,
  FriendRequestListResponse,
  SendFriendRequestInput,
  UserSearchHit,
} from './types';

export interface ListFriendsArgs {
  search?: string;
  limit?: number;
}

export interface ListRequestsArgs {
  limit?: number;
}

export const friendsApi = {
  /**
   * List accepted friends. Returns `{ items, total, next_cursor }` from the
   * backend, unwrapped to a plain array for the caller.
   */
  async list(
    getToken: () => string | null,
    args: ListFriendsArgs = {},
  ): Promise<FriendListResponse> {
    const params = new URLSearchParams();
    if (args.search) params.set('search', args.search);
    if (args.limit) params.set('limit', String(args.limit));
    const query = params.toString();
    const { items, nextCursor } = await fetchJsonList<FriendEntry>(
      `/friends${query ? `?${query}` : ''}`,
      getToken,
    );
    return { rows: items ?? [], nextCursor: nextCursor ?? null };
  },

  /**
   * Search registered users by username prefix (or email fragment). Backend
   * wraps the response in `{ items }` so we route through `fetchJsonList`.
   */
  async search(getToken: () => string | null, q: string, limit = 20): Promise<UserSearchHit[]> {
    const params = new URLSearchParams();
    params.set('q', q);
    params.set('limit', String(limit));
    const { items } = await fetchJsonList<UserSearchHit>(
      `/friends/search?${params.toString()}`,
      getToken,
    );
    return items ?? [];
  },

  async listRequests(
    getToken: () => string | null,
    args: ListRequestsArgs = {},
  ): Promise<FriendRequestListResponse> {
    const params = new URLSearchParams();
    if (args.limit) params.set('limit', String(args.limit));
    const query = params.toString();
    const data = await fetchJson<FriendRequestListResponse>(
      `/friends/requests${query ? `?${query}` : ''}`,
      getToken,
    );
    return data ?? { incoming: [], outgoing: [] };
  },

  async sendRequest(
    getToken: () => string | null,
    input: SendFriendRequestInput,
  ): Promise<{ id: string }> {
    return await fetchJson<{ id: string }>(`/friends/requests`, getToken, {
      method: 'POST',
      body: input,
    });
  },

  async acceptRequest(getToken: () => string | null, id: string) {
    await fetchJson<unknown>(`/friends/requests/${encodeURIComponent(id)}/accept`, getToken, {
      method: 'POST',
      body: {},
    });
  },

  async rejectRequest(getToken: () => string | null, id: string) {
    await fetchJson<unknown>(`/friends/requests/${encodeURIComponent(id)}/reject`, getToken, {
      method: 'POST',
      body: {},
    });
  },

  async cancelRequest(getToken: () => string | null, id: string) {
    await fetchJson<unknown>(`/friends/requests/${encodeURIComponent(id)}`, getToken, {
      method: 'DELETE',
    });
  },

  async removeFriend(getToken: () => string | null, friendUserId: string) {
    await fetchJson<unknown>(`/friends/${encodeURIComponent(friendUserId)}`, getToken, {
      method: 'DELETE',
    });
  },
};

export type { FriendEntry, FriendRequestEntry, UserSearchHit };
